"use client";

import { AppLink as Link } from "@/components/AppLink";
import { usePathname } from "next/navigation";
import { useState } from "react";
import {
  ChevronLeft, ChevronRight, LayoutDashboard, Kanban, FolderOpen,
  CalendarDays, Receipt, ShieldAlert, XCircle, Users, Activity,
  Database, Settings2, Mail, UserCircle,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

type NavItem = { href: string; label: string; icon: React.ElementType };

export const OPERATIVA: NavItem[] = [
  { href: "/", label: "Inicio", icon: LayoutDashboard },
  { href: "/emision", label: "Emisión", icon: Kanban },
  { href: "/pre-clasicos", label: "Pre-Clásicos", icon: Kanban },
  { href: "/calendario", label: "Calendario", icon: CalendarDays },
  { href: "/emision/control-recibos", label: "Recibos", icon: Receipt },
  { href: "/archivo", label: "Archivo", icon: FolderOpen },
];

export const HERRAMIENTAS: NavItem[] = [
  { href: "/scoring", label: "Scoring", icon: ShieldAlert },
  { href: "/canceladas", label: "Canceladas", icon: XCircle },
  { href: "/perfil", label: "Mi perfil", icon: UserCircle },
];

export const ADMIN: NavItem[] = [
  { href: "/admin/usuarios", label: "Usuarios", icon: Users },
  { href: "/admin/metricas", label: "Métricas", icon: Activity },
  { href: "/admin/fast360", label: "Fast360", icon: Database },
  { href: "/admin/ajustes", label: "Ajustes", icon: Settings2 },
  { href: "/admin/ajustes/plantillas-email", label: "Plantillas", icon: Mail },
];

function NavGroup({
  title,
  items,
  collapsed,
  pathname,
}: {
  title: string;
  items: NavItem[];
  collapsed: boolean;
  pathname: string;
}) {
  if (items.length === 0) return null;
  const isActive = (href: string) => {
    if (href === "/") return pathname === "/";
    if (href === "/emision") return pathname === "/emision" || (pathname.startsWith("/emision/") && !pathname.startsWith("/emision/control-recibos"));
    if (href === "/admin/ajustes") return pathname === "/admin/ajustes";
    return pathname === href || pathname.startsWith(href + "/");
  };

  return (
    <div className="mb-3">
      {!collapsed && (
        <p className="px-3 mb-1 text-[10px] font-bold uppercase tracking-wider text-sidebar-foreground/40">
          {title}
        </p>
      )}
      <div className="space-y-0.5">
        {items.map(({ href, label, icon: Icon }) => {
          const active = isActive(href);
          return (
            <Link
              key={href}
              href={href}
              title={label}
              className={cn(
                "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                active
                  ? "bg-segurclassic-green/10 text-segurclassic-green dark:text-segurclassic-gold"
                  : "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground",
              )}
            >
              <Icon className="h-4 w-4 shrink-0" />
              {!collapsed && <span className="truncate">{label}</span>}
            </Link>
          );
        })}
      </div>
    </div>
  );
}

export function Sidebar() {
  const pathname = usePathname();
  const { usuarioRPA } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const isAdmin = usuarioRPA?.rol === "admin";

  return (
    <aside
      className={cn(
        "hidden md:flex flex-col shrink-0 border-r border-sidebar-border bg-sidebar transition-all duration-300 ease-in-out",
        collapsed ? "w-16" : "w-52",
      )}
    >
      <nav className="flex-1 py-3 px-2 overflow-y-auto">
        <NavGroup title="Operativa" items={OPERATIVA} collapsed={collapsed} pathname={pathname} />
        <NavGroup title="Herramientas" items={HERRAMIENTAS} collapsed={collapsed} pathname={pathname} />
        {isAdmin && <NavGroup title="Admin" items={ADMIN} collapsed={collapsed} pathname={pathname} />}
      </nav>

      <div className="p-2 border-t border-sidebar-border">
        <button
          type="button"
          onClick={() => setCollapsed(!collapsed)}
          className="flex items-center justify-center w-full py-2 rounded-lg text-muted-foreground hover:bg-sidebar-accent hover:text-foreground transition-colors"
          aria-label={collapsed ? "Expandir sidebar" : "Colapsar sidebar"}
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>
      </div>
    </aside>
  );
}
